/**
 * URLs that arrive from outside the product — an article link off a feed, the
 * provenance link on a planned post.
 *
 * Both helpers treat the value as untrusted: a link is only rendered when it
 * parses and speaks http(s), so nothing on screen can become a `javascript:`
 * href because a feed said so.
 */

/** Schemes a link on this site is allowed to point at. */
const LINKABLE_PROTOCOLS = new Set(['http:', 'https:']);

function parse(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

/**
 * The URL back, but only when it is a real http(s) address. Anything else —
 * a relative path, a `data:` blob, an unparsable string — is not linkable and
 * comes back null, so the caller renders text or nothing at all.
 */
export function safeExternalUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = parse(value);
  if (parsed === null || !LINKABLE_PROTOCOLS.has(parsed.protocol)) return null;
  return parsed.toString();
}

/**
 * Host of a link, without `www.` — the readable name of a source. The rest of a
 * URL is not information in a chip, and a full URL would wrap the layout.
 */
export function domainOf(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = parse(value);
  if (parsed === null) return null;
  return parsed.hostname.replace(/^www\./i, '') || null;
}
